'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useAtom } from 'jotai'
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronDownIcon,
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  Loader2Icon,
  LogOutIcon,
  XIcon,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { useAuth } from '@/features/auth/hooks'
import { PlanLockBadge } from '@/features/billing/components/plan-lock-badge'
import {
  DeriveDepositPrompt,
  DeriveSubaccountPicker,
} from '@/features/dashboard/components/account-connection/derive-subaccount-picker'
import { HyperliquidSubaccountPicker } from '@/features/dashboard/components/account-connection/hyperliquid-subaccount-picker'
import { LighterSubaccountPicker } from '@/features/dashboard/components/account-connection/lighter-subaccount-picker'
import { PapertradeSignStep } from '@/features/dashboard/components/account-connection/papertrade-sign-step'
import { useCapabilities } from '@/hooks/use-capabilities'
import { useDeriveOnboard } from '@/hooks/use-derive-onboard'
import { useHyperliquidOnboard } from '@/hooks/use-hyperliquid-onboard'
import { useLighterIntegratorConsent } from '@/hooks/use-lighter-integrator-consent'
import { useLighterOnboard } from '@/hooks/use-lighter-onboard'
import { useVenueGate } from '@/hooks/use-venue-gate'
import { useVenueOauthConnect } from '@/hooks/use-venue-oauth-connect'
import { trackAccountConnected } from '@/lib/analytics-account'
import { BILLING_ROUTE } from '@/lib/billing/routes'
import { copyText } from '@/lib/clipboard'
import {
  getExchangeByTradeVenue,
  SUPPORTING_EXCHANGES,
  type SupportingExchangeEntry,
} from '@/lib/configs/exchange-registry'
import { connectWallet, signDeriveLogin } from '@/lib/derive/sign-session-key'
import {
  empty as emptySelection,
  registrationScope,
  type SelectionState,
} from '@/lib/derive/subaccount-selection'
import {
  empty as emptyHlSelection,
  type HlSelectionState,
} from '@/lib/hyperliquid/subaccount-selection'
import { beginManualWalletAction } from '@/lib/lighter/sign-subaccount-key'
import {
  empty as emptyLighterSelection,
  type SelectionState as LighterSelectionState,
} from '@/lib/lighter/subaccount-selection'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { findResumableAgentWalletAccount } from '@/lib/utils/exchange-util'
import { resolveApiKeyGuide } from '@/lib/venues/api-key-guide'
import { preloadWallet } from '@/lib/wallet/connect'
import { createAccount, fetchUserAccounts, updateAccount } from '@/service/account.api'
import { useUserAccounts } from '@/service/account.hook'
import { registerReloadBlockerSource } from '@/service/runtime-health/reload-blocker-registry'
import { useVenueApiKeyGuide, useVenues } from '@/service/venue.hook'
import { exchangeAtom } from '@/state/storage'
import {
  AccountAuthMethod,
  AccountStatus,
  type PublicAccount,
} from '@/types/generated/common/account'
import { TradeVenue } from '@/types/generated/common/types'
import type {
  ApiKeyGuide,
  DeriveLinkSubaccountsResponse,
  DeriveListSubaccountsResponse,
  HyperliquidLinkSubaccountsResponse,
  HyperliquidListSubaccountsResponse,
  LighterLinkSubaccountsResponse,
  LighterListSubaccountsResponse,
  OauthConfig,
} from '@/types/generated/public_api'

function createCredentialsSchema(messages: { apiKeyRequired: string; apiSecretRequired: string }) {
  return z.object({
    apiKey: z.string().min(1, messages.apiKeyRequired),
    apiSecret: z.string().min(1, messages.apiSecretRequired),
  })
}

// Pull a human-readable message off an unknown thrown value. Service wrappers
// reject with either a real Error or the proto Error shape ({ message }); fall
// back to the caller's copy when neither carries a usable string.
function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message
  const message = (error as { message?: unknown } | null | undefined)?.message
  return typeof message === 'string' ? message : fallback
}

// A batch Derive link can outrun the server's 30s /api TimeoutLayer while it
// keeps creating subaccounts in the background. Detect the timeout-shaped
// rejection (axios abort, HTTP 408, or a timeout message) so we can steer the
// user to re-list instead of surfacing a raw transport error.
function isTimeoutError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false
  const e = error as {
    code?: unknown
    status?: unknown
    message?: unknown
    response?: { status?: unknown } | null
  }
  if (e.code === 'ECONNABORTED') return true
  if (e.status === 408 || e.response?.status === 408) return true
  const message = typeof e.message === 'string' ? e.message.toLowerCase() : ''
  return message.includes('timeout') || message.includes('timed out')
}

type CredentialsFormData = z.infer<ReturnType<typeof createCredentialsSchema>>
type WizardStep =
  | 'select'
  | 'credentials'
  | 'derive-picker'
  | 'derive-results'
  | 'hyperliquid-picker'
  | 'hyperliquid-results'
  | 'lighter-picker'
  | 'lighter-results'
  | 'papertrade-sign'
  | 'success'
  | 'pending'

type WorkingAccount = {
  accountId: string
  accountName: string
  exchangeLabel: string
  tradeVenue: TradeVenue
  publicIp: string
  authMethod: AccountAuthMethod
}

type AccountConnectionWizardProps = {
  className?: string
  variant?: 'page' | 'sheet'
  headerLeading?: React.ReactNode
  popoverContainer?: Element | null
  /** Preselect this venue in the picker (e.g. returning from an upgrade). */
  initialVenue?: TradeVenue
  /**
   * Called instead of routing to /billing when a locked venue is picked. Lets
   * onboarding handle the upsell in-place (show its plans step) and return here
   * with the venue unlocked. Falls back to /billing navigation when absent.
   */
  onRequestUpgrade?: (venue: TradeVenue) => void
  onComplete?: (account: PublicAccount) => void
}

const DEFAULT_EXCHANGE = SUPPORTING_EXCHANGES.find((exchange) => exchange.key === 'deribit') ?? null

// How long the venue picker waits on the plan check before letting the backend decide.
const PLAN_CHECK_TIMEOUT_MS = 5_000

// Derive activates an account up to ~1 min after its subaccounts are linked. The page
// waits it out on the pending step instead of routing to /trade, where the dashboard's
// "no active account → /onboarding" gate would bounce the user straight back.
const ACTIVATION_POLL_INTERVAL_MS = 3_000
const ACTIVATION_POLL_ATTEMPTS = 40 // ~2 min

// The venue being connected, kept for the tab session: connecting a wallet venue on mobile
// leaves for the wallet app, and the browser often reloads the tab meanwhile — without this
// the wizard came back on Deribit and the user had to find their venue again (the existing
// resume path then picks up the half-created account once it's selected).
const IN_PROGRESS_VENUE_KEY = 'account-connection:in-progress-venue'

function readInProgressVenue(): TradeVenue | null {
  try {
    const raw = window.sessionStorage.getItem(IN_PROGRESS_VENUE_KEY)
    const venue = raw === null ? NaN : Number(raw)
    return SUPPORTING_EXCHANGES.some((exchange) => exchange.id === venue)
      ? (venue as TradeVenue)
      : null
  } catch {
    return null
  }
}

function writeInProgressVenue(venue: TradeVenue | null) {
  try {
    if (venue === null) window.sessionStorage.removeItem(IN_PROGRESS_VENUE_KEY)
    else window.sessionStorage.setItem(IN_PROGRESS_VENUE_KEY, String(venue))
  } catch {
    // Storage blocked (private mode): the wizard just falls back to its default venue.
  }
}

function useApiKeyGuide(venue: TradeVenue): ApiKeyGuide | undefined {
  return resolveApiKeyGuide(useVenueApiKeyGuide(venue), venue)
}

function getVenueIconKey(venue: TradeVenue): string {
  return getExchangeByTradeVenue(venue)?.key ?? 'deribit'
}

function getSelectorClassName({
  selected,
  isPageLayout,
}: {
  selected: boolean
  isPageLayout: boolean
}) {
  return cn(
    'flex h-11 w-full cursor-pointer items-center gap-3 rounded-sm px-3 text-left text-sm transition-colors',
    selected
      ? 'border-foreground/30 bg-foreground/10'
      : isPageLayout
        ? 'border-foreground/20 bg-foreground/5 hover:bg-foreground/10'
        : 'border-white/10 bg-white/5 hover:bg-white/10'
  )
}

function getPopoverContentClassName(_isPageLayout: boolean) {
  return 'menu-surface z-[70] w-[var(--radix-popover-trigger-width)]'
}

function SelectChevron({ open, isPageLayout }: { open: boolean; isPageLayout: boolean }) {
  return (
    <ChevronDownIcon
      className={cn(
        'ml-auto size-4 shrink-0 text-white/40 transition-transform',
        open && 'rotate-180',
        isPageLayout && 'text-foreground/40'
      )}
    />
  )
}

function SelectorPlaceholder({
  children,
  isPageLayout,
}: {
  children: React.ReactNode
  isPageLayout: boolean
}) {
  return (
    <span className={cn('text-white/40', isPageLayout && 'text-foreground/40')}>{children}</span>
  )
}

/** A venue option in the picker, carrying its plan-gate state for the upsell. */
type ExchangeOption = {
  exchange: SupportingExchangeEntry
  /** True when the current plan can't connect this venue (render disabled + upsell). */
  locked: boolean
  /** Cheapest plan that unlocks it (e.g. "Basic"); absent → generic upgrade prompt. */
  requiredPlanLabel?: string
  /** The plan check hasn't answered yet: not selectable for a moment, but never shown as an
   * upsell — loading is not a lock. */
  pending?: boolean
}

type ExchangeSelectPopoverProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: ExchangeOption[]
  /** The plan check is still in flight (see `pending` on the options). */
  checkingPlan?: boolean
  selectedExchange: SupportingExchangeEntry | null
  isPageLayout: boolean
  popoverContainer?: Element | null
  onSelect: (exchange: SupportingExchangeEntry) => void
  /** Invoked with the locked venue when it's clicked — routes the user to upgrade. */
  onUpgrade: (venue: TradeVenue) => void
}

function ExchangeSelectPopover({
  open,
  onOpenChange,
  options,
  checkingPlan = false,
  selectedExchange,
  isPageLayout,
  popoverContainer,
  onSelect,
  onUpgrade,
}: ExchangeSelectPopoverProps) {
  const t = useTranslations('accountConnection')
  const tb = useTranslations('billing')

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={getSelectorClassName({ selected: Boolean(selectedExchange), isPageLayout })}
        >
          {selectedExchange ? (
            <>
              <ExchangeIcon exchange={selectedExchange.key} className="size-5 rounded-full" />
              <span>{selectedExchange.label}</span>
            </>
          ) : (
            <SelectorPlaceholder isPageLayout={isPageLayout}>
              {t('selectExchange')}
            </SelectorPlaceholder>
          )}
          <SelectChevron open={open} isPageLayout={isPageLayout} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        container={popoverContainer}
        className={getPopoverContentClassName(isPageLayout)}
      >
        <div className="flex flex-col gap-1">
          {checkingPlan && (
            <div
              role="status"
              className="text-foreground/50 flex h-8 items-center gap-2 px-2 text-xs"
            >
              <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
              {t('checkingPlan')}
            </div>
          )}
          {options.map(({ exchange, locked, requiredPlanLabel, pending }) => {
            const selected = selectedExchange?.id === exchange.id
            return (
              <button
                key={exchange.id}
                type="button"
                disabled={pending}
                onClick={() => (locked ? onUpgrade(exchange.id) : onSelect(exchange))}
                className={cn(
                  'flex h-10 cursor-pointer items-center gap-3 px-2 text-left text-sm transition-colors disabled:cursor-wait disabled:opacity-60',
                  selected
                    ? 'bg-foreground/10'
                    : isPageLayout
                      ? 'hover:bg-foreground/10'
                      : 'hover:bg-white/10'
                )}
              >
                <ExchangeIcon
                  exchange={exchange.key}
                  className={cn('size-5 rounded-full', locked && 'opacity-40')}
                />
                <span className={cn(locked && 'opacity-40')}>{exchange.label}</span>
                {locked ? (
                  <PlanLockBadge
                    className="ml-auto"
                    label={
                      requiredPlanLabel
                        ? tb('requiresPlan', { plan: requiredPlanLabel })
                        : tb('requiresUpgrade')
                    }
                  />
                ) : (
                  selected && <CheckIcon className="text-foreground ml-auto size-4 shrink-0" />
                )}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function WorkingAccountCard({
  workingAccount,
  isPageLayout,
  isSubmitting,
  onChange,
}: {
  workingAccount: WorkingAccount
  isPageLayout: boolean
  isSubmitting: boolean
  onChange: () => void
}) {
  const t = useTranslations('accountConnection')

  return (
    <div
      className={cn([
        'border border-white/10 bg-white/5 p-3 text-left shadow-sm',
        isPageLayout && 'border-foreground/10 bg-foreground/5',
      ])}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn([
              'flex size-10 shrink-0 items-center justify-center bg-white/10',
              isPageLayout && 'bg-foreground/10',
            ])}
          >
            <ExchangeIcon
              exchange={getVenueIconKey(workingAccount.tradeVenue)}
              className="size-6 rounded-full"
            />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <p className="truncate text-sm font-medium">{workingAccount.accountName}</p>
            <p className="text-xs tracking-tight uppercase opacity-60">
              {workingAccount.exchangeLabel}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant={isPageLayout ? 'subtle' : 'ghost'}
          size="sm"
          onClick={onChange}
          disabled={isSubmitting}
        >
          {t('change')}
        </Button>
      </div>
    </div>
  )
}

export function AccountConnectionWizard({
  className,
  variant = 'page',
  headerLeading,
  popoverContainer,
  initialVenue,
  onRequestUpgrade,
  onComplete,
}: AccountConnectionWizardProps) {
  const tCommon = useTranslations('common')
  const t = useTranslations('accountConnection')
  const queryClient = useQueryClient()
  const router = useRouter()
  const searchParams = useSearchParams()
  const fromTradePath = searchParams.get('from')
  const cameFromTrade = Boolean(fromTradePath?.startsWith('/trade'))
  const { user, logout } = useAuth()
  const { data: accountsData, refetch: refetchAccounts } = useUserAccounts({
    showDisabled: true,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    enabled: Boolean(user?.userId),
  })
  const { data: venueList } = useVenues()
  const { connect } = useVenueOauthConnect()
  useEffect(() => preloadWallet(), [])
  const {
    onboard: onboardHyperliquid,
    listSubaccounts: listHyperliquidSubaccounts,
    linkSubaccounts: linkHyperliquidSubaccounts,
  } = useHyperliquidOnboard()
  const {
    registerSessionKey: registerDeriveSessionKey,
    listSubaccounts: listDeriveSubaccounts,
    linkSubaccounts: linkDeriveSubaccounts,
  } = useDeriveOnboard()
  const { listSubaccounts: listLighterSubaccounts, linkSubaccounts: linkLighterSubaccounts } =
    useLighterOnboard()
  const [, setSavedExchange] = useAtom(exchangeAtom)
  const accounts = useMemo(
    () =>
      accountsData?.accounts.filter(
        (item) => item.status !== AccountStatus.ACCOUNT_STATUS_INACTIVE
      ) ?? [],
    [accountsData?.accounts]
  )

  // Fail-closed until the plan is known — but loading is not a lock. While capabilities are
  // pending the picker says "checking your plan" instead of badging every venue "Requires
  // <plan>" (which read as a paywall, e.g. after a mobile wallet round-trip reloaded the tab);
  // and if they're still pending after PLAN_CHECK_TIMEOUT_MS the gate turns permissive, as it
  // is everywhere else (the backend enforces plans), so the picker can never trap anyone.
  const { isCapabilitiesPending } = useCapabilities()
  const [planCheckTimedOut, setPlanCheckTimedOut] = useState(false)
  useEffect(() => {
    if (!isCapabilitiesPending) {
      setPlanCheckTimedOut(false)
      return
    }
    const timer = setTimeout(() => setPlanCheckTimedOut(true), PLAN_CHECK_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [isCapabilitiesPending])
  const checkingPlan = isCapabilitiesPending && !planCheckTimedOut
  const venueGate = useVenueGate({ allowWhileLoading: planCheckTimedOut })
  // Build the venue picker. An allowed venue shows only when the server reports
  // signups open for it (falling back to the full registry until that list
  // resolves). A venue the plan can't use shows only as an upsell when a paid
  // plan actually unlocks it — disabled with a "Requires <plan>" badge rather
  // than hidden, so FREE users can see (and upgrade into) what they're missing.
  // A venue that is neither (signup-closed, or locked with no upgrade path) is
  // omitted: never a dead-end upsell, never a venue the plan can't connect.
  const exchangeOptions = useMemo<ExchangeOption[]>(() => {
    const openForSignup = (venue: TradeVenue) =>
      !venueList?.venues.length ||
      venueList.venues.some((v) => v.tradeVenue === venue && v.newAccount)
    return SUPPORTING_EXCHANGES.flatMap((exchange): ExchangeOption[] => {
      if (checkingPlan) {
        return openForSignup(exchange.id) ? [{ exchange, locked: false, pending: true }] : []
      }
      const { allowed, upgradePlanLabel } = venueGate(exchange.id)
      const show = allowed ? openForSignup(exchange.id) : upgradePlanLabel !== undefined
      if (!show) return []
      return [{ exchange, locked: !allowed, requiredPlanLabel: upgradePlanLabel }]
    })
  }, [checkingPlan, venueList, venueGate])

  // Default to Deribit (FREE-allowed) unless a venue was preselected — e.g. the
  // onboarding flow returning the user here after they upgraded to unlock it — or one was
  // being connected when the tab reloaded (readInProgressVenue).
  const initialExchange = useMemo(() => {
    const venue = initialVenue ?? readInProgressVenue()
    return venue != null
      ? (SUPPORTING_EXCHANGES.find((exchange) => exchange.id === venue) ?? DEFAULT_EXCHANGE)
      : DEFAULT_EXCHANGE
  }, [initialVenue])

  const [step, setStep] = useState<WizardStep>('select')
  // Connected: nothing left to resume.
  useEffect(() => {
    if (step === 'success') writeInProgressVenue(null)
  }, [step])
  // Leaving the wizard in-app (finished and routed on, or the sheet closed) ends the attempt
  // too. A tab the browser discards or reloads mid-wallet-trip runs no cleanup, so that —
  // the case this exists for — keeps it.
  useEffect(() => () => writeInProgressVenue(null), [])
  const [selectedExchange, setSelectedExchange] = useState<SupportingExchangeEntry | null>(
    initialExchange
  )
  const [accountName, setAccountName] = useState('')
  const [workingAccount, setWorkingAccount] = useState<WorkingAccount | null>(null)
  const isHyperliquid = workingAccount?.tradeVenue === TradeVenue.TRADE_VENUE_HYPERLIQUID
  const isDerive = workingAccount?.tradeVenue === TradeVenue.TRADE_VENUE_DERIVE
  const isLighter = workingAccount?.tradeVenue === TradeVenue.TRADE_VENUE_LIGHTER
  // Papertrade: the wallet connected for the sign step.
  const [papertradeWallet, setPapertradeWallet] = useState<string | null>(null)
  const [readyAccount, setReadyAccount] = useState<PublicAccount | null>(null)
  // What the 'pending' step is waiting on: API-key verification (retry = re-enter keys),
  // or a linked Derive account still activating (polled; retry = check again).
  const [pendingReason, setPendingReason] = useState<'verification' | 'activation'>('verification')
  const [activationCheck, setActivationCheck] = useState(0)
  const [showSecret, setShowSecret] = useState(false)
  // When a venue offers OAuth we default to it, but let the user opt into
  // pasting API keys manually instead.
  const [showManualKeys, setShowManualKeys] = useState(false)
  const [copied, setCopied] = useState(false)
  const [exchangePopoverOpen, setExchangePopoverOpen] = useState(false)
  const [isPreparing, setIsPreparing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  // Hyperliquid rejects approvals from a wallet that has never deposited, so we
  // gate the connect button on the user confirming they've funded their wallet.
  const [hasFunded, setHasFunded] = useState(false)
  // Derive: the wallet connected at the start of onboarding, reused for every
  // later signature so it doesn't prompt the wallet picker a second time for
  // the same address.
  const [deriveConnectedWallet, setDeriveConnectedWallet] = useState<string | null>(null)
  // Derive: the wallet's subaccount listing that drives the picker step, plus
  // the running picker selection and the per-slot batch-link result.
  const [deriveList, setDeriveList] = useState<DeriveListSubaccountsResponse | null>(null)
  const [deriveSelection, setDeriveSelection] = useState<SelectionState>(emptySelection)
  const [deriveLinkResult, setDeriveLinkResult] = useState<DeriveLinkSubaccountsResponse | null>(
    null
  )
  // Hyperliquid: the master wallet address the agent was just authorized
  // for (returned by onboardHyperliquid), plus the resulting sub-account
  // listing/selection/batch-link result driving the picker/results steps.
  // hyperliquidWalletPreconnected mirrors Derive's flag exactly: whether the
  // picker was reached via the fast path in handlePrepareAccount (an
  // already-connected wallet, no signature needed) rather than right after
  // a fresh authorize (handleHyperliquidConnect).
  const [hyperliquidWalletPreconnected, setHyperliquidWalletPreconnected] = useState(false)
  const [hyperliquidConnectedWallet, setHyperliquidConnectedWallet] = useState<string | null>(null)
  const [hyperliquidList, setHyperliquidList] = useState<HyperliquidListSubaccountsResponse | null>(
    null
  )
  const [hyperliquidSelection, setHyperliquidSelection] =
    useState<HlSelectionState>(emptyHlSelection)
  const [hyperliquidLinkResult, setHyperliquidLinkResult] =
    useState<HyperliquidLinkSubaccountsResponse | null>(null)
  // Lighter: the L1 wallet address connected during prepare (used for both
  // listing and linking — Lighter has no separate agent/session-key concept,
  // so there's no walletConnected fast-path or preconnected flag to mirror
  // Derive/Hyperliquid's), plus the resulting sub-account listing/selection/
  // batch-link result driving the picker/results steps. Per-slot key minting
  // (prepare/sign/confirm) happens inside LighterSubaccountPicker itself.
  const [lighterL1Address, setLighterL1Address] = useState('')
  const [lighterList, setLighterList] = useState<LighterListSubaccountsResponse | null>(null)
  const [lighterSelection, setLighterSelection] =
    useState<LighterSelectionState>(emptyLighterSelection)
  const [lighterLinkResult, setLighterLinkResult] = useState<LighterLinkSubaccountsResponse | null>(
    null
  )
  // Tracks mount so a slow Derive link (which can outlive this component when
  // the sheet is dismissed mid-request) doesn't setState after unmount.
  const isMountedRef = useRef(true)
  // Accounts already reported as connected by this wizard instance (analytics dedupe).
  const trackedConnectionsRef = useRef(new Set<string>())
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Tells runtime-recovery not to reload out from under an in-progress
  // wallet-onboarding flow (unsubmitted API keys, a wallet mid-signature, an
  // unsubmitted subaccount selection) — none of that is server-durable until
  // the flow reaches 'success', so losing it to a silent Level 3 reload would
  // be silent data loss, not just a stale UI. 'select' (nothing chosen yet)
  // and 'success'/'pending' (already submitted server-side) are safe to
  // reload through. Read through refs so this doesn't re-register on every
  // step/state change — isBlocking() is only called on demand.
  const stepRef = useRef(step)
  stepRef.current = step
  const isPreparingRef = useRef(isPreparing)
  isPreparingRef.current = isPreparing
  const isSubmittingRef = useRef(isSubmitting)
  isSubmittingRef.current = isSubmitting
  useEffect(
    () =>
      registerReloadBlockerSource({
        reason: 'account-connection-in-progress',
        isBlocking: () =>
          isPreparingRef.current ||
          isSubmittingRef.current ||
          !['select', 'success', 'pending'].includes(stepRef.current),
      }),
    []
  )

  const isPageLayout = variant === 'page'
  const credentialsSchema = useMemo(
    () =>
      createCredentialsSchema({
        apiKeyRequired: t('validation.apiKeyRequired'),
        apiSecretRequired: t('validation.apiSecretRequired'),
      }),
    [t]
  )

  const credentialsForm = useForm<CredentialsFormData>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { apiKey: '', apiSecret: '' },
  })

  useEffect(() => {
    if (!isPageLayout) return

    setStep('select')
    setSelectedExchange(initialExchange)
    setAccountName('')
    setWorkingAccount(null)
    setReadyAccount(null)
    setPapertradeWallet(null)
    setShowSecret(false)
    setShowManualKeys(false)
    setCopied(false)
    setErrorMessage('')
    setHasFunded(false)
    setDeriveConnectedWallet(null)
    setDeriveList(null)
    setDeriveSelection(emptySelection())
    setDeriveLinkResult(null)
    credentialsForm.reset()
    setSavedExchange(null)
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] === 'accounts' && query.queryKey[1] !== user?.userId,
    })
  }, [credentialsForm, initialExchange, isPageLayout, queryClient, setSavedExchange, user?.userId])

  // OAuth onboarding applies when the server's venue registry carries an
  // OAuth config for the working account's venue (Bybit broker flow).
  // Absent config means manual API-key entry.
  const oauthConfig: OauthConfig | undefined = useMemo(
    () =>
      workingAccount
        ? venueList?.venues.find((venue) => venue.tradeVenue === workingAccount.tradeVenue)?.oauth
        : undefined,
    [venueList, workingAccount]
  )

  // The initiating account still needs its own subaccount when the server
  // reports it has none yet. `initiatingSubaccountId` is authoritative — it's
  // the account's stored derive_subaccount_id (ts-proto int64 → decimal
  // string; '0' = none yet). The old client-side linkedAccountId scan is kept
  // only as a fallback for a server too old to send the field (which, since
  // ts-proto decodes an absent int64 to '0', collapses to the same result).
  const initiatingSubaccountId = deriveList?.initiatingSubaccountId ?? '0'
  const initiatingNeedsLink = useMemo(() => {
    if (deriveList == null || workingAccount == null) return false
    // A non-zero id is authoritative: the account already has a subaccount.
    if (initiatingSubaccountId !== '0') return false
    // '0' means "genuinely none yet" OR "server too old to send the field";
    // both are settled correctly by the pre-existing linkedAccountId scan.
    return !deriveList.subaccounts.some((sub) => sub.linkedAccountId === workingAccount.accountId)
  }, [deriveList, workingAccount, initiatingSubaccountId])

  const title = variant === 'sheet' ? t('titleSheet') : t('titlePage')
  const subtitle = variant === 'sheet' ? t('subtitleSheet') : t('subtitlePage')

  const selectedDisplayName = useMemo(() => {
    if (!selectedExchange) return ''
    return accountName.trim() || selectedExchange.label
  }, [accountName, selectedExchange])

  const handleSelectExchange = (exchange: SupportingExchangeEntry) => {
    writeInProgressVenue(exchange.id)
    setSelectedExchange(exchange)
    setAccountName('')
    setErrorMessage('')
    setExchangePopoverOpen(false)
  }

  const handlePrepareAccount = async () => {
    if (!selectedExchange) return

    setIsPreparing(true)
    setErrorMessage('')

    try {
      const isDeriveExchange = selectedExchange.id === TradeVenue.TRADE_VENUE_DERIVE
      const isHyperliquidExchange = selectedExchange.id === TradeVenue.TRADE_VENUE_HYPERLIQUID
      const isLighterExchange = selectedExchange.id === TradeVenue.TRADE_VENUE_LIGHTER
      const isPapertradeExchange = selectedExchange.id === TradeVenue.TRADE_VENUE_PAPERTRADE
      // Connect the wallet FIRST, before any other awaited network call in
      // this handler — the click that invoked this handler grants a
      // short-lived browser "user activation" window that intervening
      // awaits (account creation, cache invalidation) do not renew, so the
      // wallet extension's connect prompt has to run as close to the click
      // as possible or it can intermittently reject with "must be started
      // from a user action". Derive, Hyperliquid, and Lighter share the same
      // underlying connectWallet() (all three re-export it from
      // lib/wallet/connect).
      const connectedWalletAddress =
        isDeriveExchange || isHyperliquidExchange || isLighterExchange || isPapertradeExchange
          ? await connectWallet()
          : undefined

      // Agent-wallet venues (Hyperliquid) are one-per-user and onboard over
      // several signature attempts. If a half-onboarded CREATING account
      // already exists for this venue, resume it rather than mint a duplicate.
      // The backend now dedupes create too, so this is belt-and-suspenders.
      const resumableAccount = findResumableAgentWalletAccount(accounts, selectedExchange.id)
      let response: Awaited<ReturnType<typeof createAccount>> | undefined

      if (resumableAccount) {
        setWorkingAccount({
          accountId: resumableAccount.accountId,
          accountName: accountName.trim() || resumableAccount.name,
          exchangeLabel: selectedExchange.label,
          tradeVenue: resumableAccount.tradeVenue,
          publicIp: resumableAccount.publicIp || '',
          authMethod: resumableAccount.authMethod,
        })
      } else {
        const name = accountName.trim() || selectedExchange.label
        response = await createAccount({
          name,
          tradeVenue: selectedExchange.id,
        })

        if (!response?.accountId) {
          throw new Error(t('errors.failedCreateAccount'))
        }

        setWorkingAccount({
          accountId: response.accountId,
          accountName: name,
          exchangeLabel: selectedExchange.label,
          tradeVenue: selectedExchange.id,
          publicIp: response.publicIp || '',
          authMethod: AccountAuthMethod.ACCOUNT_AUTH_METHOD_UNSPECIFIED,
        })
      }

      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      await refetchAccounts()
      setShowManualKeys(false)

      const accountIdForDerive = isDeriveExchange
        ? (resumableAccount?.accountId ?? response?.accountId)
        : undefined
      const accountIdForHyperliquid = isHyperliquidExchange
        ? (resumableAccount?.accountId ?? response?.accountId)
        : undefined
      const accountIdForLighter = isLighterExchange
        ? (resumableAccount?.accountId ?? response?.accountId)
        : undefined

      if (isDeriveExchange && connectedWalletAddress && accountIdForDerive) {
        setDeriveConnectedWallet(connectedWalletAddress)
        // The wallet signs a login timestamp and Derive lists its own
        // subaccounts on it. Nothing is registered yet, and nothing needs to
        // be: choosing comes first precisely so the session key can name the
        // subaccounts the customer chose and no others.
        const login = await signDeriveLogin(connectedWalletAddress)
        const list = await listDeriveSubaccounts(accountIdForDerive, connectedWalletAddress, login)
        setDeriveList(list)
        setDeriveSelection(emptySelection())
        setStep('derive-picker')
      } else if (isHyperliquidExchange && connectedWalletAddress && accountIdForHyperliquid) {
        // Mirrors the Derive branch above: list the wallet's sub-accounts up
        // front. A live, unexpired agent (wallet_connected) means this exact
        // user already has an anchor for this wallet — skip signing entirely
        // and jump straight to the picker so reconnecting the same wallet to
        // add more sub-accounts doesn't require (and can't be blocked by)
        // prepare_agent's "already connected elsewhere" pre-check. Otherwise
        // fall through to the credentials screen's "Connect wallet &
        // authorize" button (handleHyperliquidConnect), unchanged.
        const list = await listHyperliquidSubaccounts(
          accountIdForHyperliquid,
          connectedWalletAddress
        )
        if (list.walletConnected) {
          setHyperliquidConnectedWallet(connectedWalletAddress)
          setHyperliquidList(list)
          setHyperliquidSelection(emptyHlSelection())
          setHyperliquidWalletPreconnected(true)
          setStep('hyperliquid-picker')
        } else {
          setStep('credentials')
        }
      } else if (isLighterExchange && connectedWalletAddress && accountIdForLighter) {
        // Lighter has no agent/session-key registration step — the L1
        // address's sub-accounts are listable unauthenticated, so this jumps
        // straight to the picker (per-slot key minting happens there).
        setLighterL1Address(connectedWalletAddress)
        const list = await listLighterSubaccounts(accountIdForLighter, connectedWalletAddress)
        setLighterList(list)
        setLighterSelection(emptyLighterSelection())
        setStep('lighter-picker')
      } else if (isPapertradeExchange) {
        // No API keys and no picker: the only step is signing the session key
        // with the wallet connected above. Never fall through to 'credentials'.
        const papertradeAccountId = resumableAccount?.accountId ?? response?.accountId
        if (!connectedWalletAddress || !papertradeAccountId) {
          throw new Error(t('errors.failedPrepareAccountDescription'))
        }
        setPapertradeWallet(connectedWalletAddress)
        setStep('papertrade-sign')
      } else if (isLighterExchange) {
        // Lighter must never fall through to 'credentials': every block that
        // step renders excludes Lighter (the manual API-key form is gated on
        // `!isLighter`), so the user would land on a sheet showing only the
        // "paste your API keys" subtitle with no form under it and no way out
        // but Back. Reaching here means the wallet connect resolved without an
        // address — surface that instead of a dead end.
        throw new Error(t('errors.failedPrepareAccountDescription'))
      } else {
        setStep('credentials')
      }
    } catch (error) {
      console.error('Failed to prepare account connection:', error)
      setErrorMessage(extractErrorMessage(error, t('errors.failedPrepareAccountDescription')))
      toast.error(t('errors.failedPrepareAccount'))
    } finally {
      setIsPreparing(false)
    }
  }

  const handleCopyIp = async () => {
    if (!workingAccount?.publicIp) {
      toast.error(t('errors.noIpAddressToCopy'))
      return
    }

    if (!(await copyText(workingAccount.publicIp))) return
    setCopied(true)
    toast.success(t('ipAddressCopied'))
    setTimeout(() => setCopied(false), 2000)
  }

  // Poll the network directly rather than through react-query's cache — going via
  // queryClient.fetchQuery with a one-off key meant the very first read could miss
  // invalidation (no cache entry yet to invalidate) and later reads risked resolving
  // against a stale snapshot instead of a guaranteed fresh network hit, which is what
  // made a fast backend AUTH_FAILED response show up slowly (or never) in the UI.
  const waitForVerifiedAccount = async (accountId: string) => {
    let verifiedAccount: PublicAccount | undefined

    for (let attempt = 0; attempt < 8; attempt++) {
      const { accounts: updatedAccounts } = await fetchUserAccounts()

      verifiedAccount = updatedAccounts.find((account) => account.accountId === accountId)

      if (verifiedAccount?.status === AccountStatus.ACCOUNT_STATUS_ACTIVE) {
        return verifiedAccount
      }

      if (verifiedAccount?.status === AccountStatus.ACCOUNT_STATUS_AUTH_FAILED) {
        throw new Error(
          verifiedAccount.lastAuthError
            ? t('errors.apiAuthenticationFailedReason', { reason: verifiedAccount.lastAuthError })
            : t('errors.apiAuthenticationFailedDescription')
        )
      }

      await new Promise((resolve) => setTimeout(resolve, 1000))
    }

    return verifiedAccount
  }

  const finalizeAccountVerification = async (accountId: string) => {
    const verifiedAccount = await waitForVerifiedAccount(accountId)
    await queryClient.invalidateQueries({ queryKey: ['accounts'] })
    await refetchAccounts()

    if (verifiedAccount?.status === AccountStatus.ACCOUNT_STATUS_ACTIVE) {
      setSavedExchange(verifiedAccount)
      setReadyAccount(verifiedAccount)
      setStep('success')
      toast.success(t('accountConnectedSuccessfully'))
      trackAccountConnected(verifiedAccount, trackedConnectionsRef.current)
      onComplete?.(verifiedAccount)
      return
    }

    setPendingReason('verification')
    setStep('pending')
    toast.info(t('verificationStillPending'))
  }

  useEffect(() => {
    if (step !== 'pending' || !workingAccount) return

    let cancelled = false

    const checkPendingAccount = async () => {
      try {
        await queryClient.invalidateQueries({ queryKey: ['accounts'] })
        const { data } = await refetchAccounts()
        if (cancelled) return

        const verifiedAccount = data?.accounts.find(
          (account) => account.accountId === workingAccount.accountId
        )

        if (verifiedAccount?.status === AccountStatus.ACCOUNT_STATUS_ACTIVE) {
          setSavedExchange(verifiedAccount)
          setReadyAccount(verifiedAccount)
          setStep('success')
          toast.success(t('accountConnectedSuccessfully'))
          trackAccountConnected(verifiedAccount, trackedConnectionsRef.current)
          onComplete?.(verifiedAccount)
        }

        if (verifiedAccount?.status === AccountStatus.ACCOUNT_STATUS_AUTH_FAILED) {
          setStep('credentials')
          setErrorMessage(
            verifiedAccount.lastAuthError
              ? t('errors.apiAuthenticationFailedReason', { reason: verifiedAccount.lastAuthError })
              : t('errors.apiAuthenticationFailedDescription')
          )
          toast.error(t('errors.apiAuthenticationFailed'))
        }
      } catch (error) {
        console.error('Failed to refresh pending account status:', error)
      }
    }

    void checkPendingAccount()
    const intervalId = window.setInterval(checkPendingAccount, 3000)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [queryClient, refetchAccounts, setSavedExchange, step, t, workingAccount, onComplete])

  const handleSubmitCredentials = async (data: CredentialsFormData) => {
    if (!workingAccount) return

    setIsSubmitting(true)
    setErrorMessage('')

    try {
      await updateAccount({
        accountId: workingAccount.accountId,
        name: workingAccount.accountName,
        apiKey: data.apiKey.trim(),
        apiSecret: data.apiSecret.trim(),
      })

      await finalizeAccountVerification(workingAccount.accountId)
    } catch (error) {
      console.error('Failed to connect account:', error)
      setErrorMessage(
        error instanceof Error ? error.message : t('errors.failedConnectAccountDescription')
      )
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOauthConnect = async () => {
    if (!workingAccount || !oauthConfig) return

    setIsSubmitting(true)
    setErrorMessage('')

    try {
      await connect({
        accountId: workingAccount.accountId,
        venue: workingAccount.tradeVenue,
        oauthConfig,
      })

      await finalizeAccountVerification(workingAccount.accountId)
    } catch (error) {
      console.error('Failed to connect account via OAuth:', error)
      const message =
        error instanceof Error
          ? error.message
          : typeof (error as { message?: unknown })?.message === 'string'
            ? (error as { message: string }).message
            : t('errors.failedConnectWithExchange', { exchange: workingAccount.exchangeLabel })
      setErrorMessage(message)
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // Verification can sit in PENDING indefinitely on the backend (e.g. wrong API
  // keys that never flip to AUTH_FAILED) — let the user bail out and re-enter
  // credentials instead of being stuck on the pending screen forever.
  const handleRetryCredentials = () => {
    setErrorMessage('')
    setShowManualKeys(true)
    credentialsForm.reset()
    setStep('credentials')
  }

  const handleHyperliquidConnect = async () => {
    if (!workingAccount) return
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const masterAddress = await onboardHyperliquid(workingAccount.accountId)
      // Agent is authorized — list the wallet's sub-accounts and hand off to
      // the picker rather than finalizing immediately. Submitting the picker
      // with zero selections (handleHyperliquidLinkSubmit) is what finishes
      // onboarding identically to today's single-account outcome.
      const list = await listHyperliquidSubaccounts(workingAccount.accountId, masterAddress)
      setHyperliquidConnectedWallet(masterAddress)
      setHyperliquidList(list)
      setHyperliquidSelection(emptyHlSelection())
      setStep('hyperliquid-picker')
    } catch (error) {
      console.error('Failed to onboard Hyperliquid account:', error)
      const message =
        error instanceof Error
          ? error.message
          : typeof (error as { message?: unknown })?.message === 'string'
            ? (error as { message: string }).message
            : 'Failed to authorize the Hyperliquid agent. Please try again.'
      setErrorMessage(message)
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // Submit the picker's selection (zero or more). Zero wallet signatures —
  // the stored agent key already covers every sub-account — so this
  // advances straight to the per-slot results screen.
  const handleHyperliquidLinkSubmit = async () => {
    if (!workingAccount || !hyperliquidConnectedWallet) return
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const result = await linkHyperliquidSubaccounts(
        workingAccount.accountId,
        hyperliquidConnectedWallet,
        hyperliquidSelection
      )
      // A successful link creates/links accounts server-side. Invalidate now,
      // before showing results, so dismissing the sheet straight from the
      // results screen still leaves a fresh account list behind.
      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      if (!isMountedRef.current) return
      setHyperliquidLinkResult(result)
      setStep('hyperliquid-results')
    } catch (error) {
      console.error('Failed to link Hyperliquid subaccounts:', error)
      if (!isMountedRef.current) return
      setErrorMessage(extractErrorMessage(error, t('hyperliquidLinkError')))
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      if (isMountedRef.current) setIsSubmitting(false)
    }
  }

  // "Adjust selection" on the results screen. The previous hyperliquidList/
  // selection are stale — just-linked rows lack their alreadyLinked flag and
  // remainingAccountSlots is off. Re-list from the server and reset the
  // selection before returning.
  const refreshHyperliquidPicker = async () => {
    if (!workingAccount || !hyperliquidConnectedWallet) return
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const list = await listHyperliquidSubaccounts(
        workingAccount.accountId,
        hyperliquidConnectedWallet
      )
      if (!isMountedRef.current) return
      setHyperliquidList(list)
      setHyperliquidSelection(emptyHlSelection())
      setStep('hyperliquid-picker')
    } catch (error) {
      console.error('Failed to refresh Hyperliquid subaccounts:', error)
      if (!isMountedRef.current) return
      setErrorMessage(extractErrorMessage(error, t('hyperliquidLinkError')))
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      if (isMountedRef.current) setIsSubmitting(false)
    }
  }

  // Leave the results screen — the sub-accounts are already linked
  // server-side, so this only needs to resolve the initiating account's own
  // verification, same as every other connect path.
  const handleFinishHyperliquid = async () => {
    if (!workingAccount) return
    await finalizeAccountVerification(workingAccount.accountId)
  }

  // Registers a session key for a selection, and only for it. The scope comes
  // from `registrationScope`, which unions the selection with what this user
  // has already linked on this wallet — a registration replaces the stored key,
  // so a narrower one would stop their existing accounts trading.
  const registerDeriveKeyFor = async (
    accountId: string,
    walletAddress: string,
    selection: SelectionState,
    list: DeriveListSubaccountsResponse
  ) => {
    const scope = registrationScope(selection, list.subaccounts, list.subaccountCreateAmountUsd)
    if (scope.length === 0) throw new Error(t('deriveScopeEmpty'))
    await registerDeriveSessionKey(accountId, walletAddress, scope)
  }

  // Submit the picker's selection. Two wallet prompts first: the session key
  // is registered for exactly these subaccounts, because Derive bakes the list
  // into the Action being signed and no later call can widen it.
  const handleDeriveLinkSubmit = async () => {
    if (!workingAccount || !deriveConnectedWallet || !deriveList) return
    // Synchronously first — transient user activation does not survive an await.
    try {
      beginManualWalletAction()
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : String(e))
      return
    }
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      await registerDeriveKeyFor(
        workingAccount.accountId,
        deriveConnectedWallet,
        deriveSelection,
        deriveList
      )
      const result = await linkDeriveSubaccounts(
        workingAccount.accountId,
        deriveConnectedWallet,
        deriveSelection
      )
      // A successful link creates/links accounts server-side. Invalidate now,
      // before showing results, so dismissing the sheet straight from the
      // results screen (without pressing Done) still leaves a fresh account
      // list behind — handleFinishDerive's own refetch covers the Done path.
      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      // The link can outlive this component (sheet dismissed mid-request);
      // don't touch state after unmount.
      if (!isMountedRef.current) return
      setDeriveLinkResult(result)
      setStep('derive-results')
    } catch (error) {
      console.error('Failed to link Derive subaccounts:', error)
      if (!isMountedRef.current) return
      // The server keeps creating subaccounts in the background past its 30s
      // /api TimeoutLayer, so a timeout is not a failure. Nudge the user to
      // re-list (which reflects whatever converged) instead of a raw error.
      if (isTimeoutError(error)) {
        toast.info(t('deriveLinkTimeout'))
        await refreshDerivePicker()
        return
      }
      // Otherwise the wrapper rejects with the proto Error shape, whose message
      // carries the actionable reason (e.g. an expired session key that no
      // retry can fix). Surface it, not a generic line.
      setErrorMessage(extractErrorMessage(error, t('deriveLinkError')))
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      if (isMountedRef.current) setIsSubmitting(false)
    }
  }

  // "Adjust selection" on the results screen. The previous deriveList/selection
  // are stale — just-linked rows lack their alreadyLinked flag, remainingSlots
  // is off, and re-submitting the old selection would re-create subaccounts.
  // Re-list from the server and reset the selection before returning. The
  // wallet's own login header, not the stored key: a key registered for part of
  // the wallet reads balances for that part only, and the picker offers all of
  // it.
  const refreshDerivePicker = async () => {
    if (!workingAccount || !deriveConnectedWallet) return
    try {
      beginManualWalletAction()
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : String(e))
      return
    }
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const login = await signDeriveLogin(deriveConnectedWallet)
      const list = await listDeriveSubaccounts(
        workingAccount.accountId,
        deriveConnectedWallet,
        login
      )
      if (!isMountedRef.current) return
      setDeriveList(list)
      setDeriveSelection(emptySelection())
      setStep('derive-picker')
    } catch (error) {
      console.error('Failed to refresh Derive subaccounts:', error)
      if (!isMountedRef.current) return
      setErrorMessage(extractErrorMessage(error, t('deriveLinkError')))
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      if (isMountedRef.current) setIsSubmitting(false)
    }
  }

  // Finish a create. A subaccount that did not exist when the customer signed
  // cannot be inside the scope they signed for, so the venue refuses to trade
  // it and the link reports the slot as needing authorisation. Re-list, name
  // the new ids in a fresh registration, and link them — the only shape a
  // create can take when the scope is honest about what it covers.
  const handleDeriveAuthorizeCreated = async () => {
    if (!workingAccount || !deriveConnectedWallet || !deriveLinkResult) return
    const pending = deriveLinkResult.slots
      .filter((slot) => !slot.ok && slot.subaccountId !== '0')
      .map((slot) => slot.subaccountId)
    if (pending.length === 0) return
    try {
      beginManualWalletAction()
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : String(e))
      return
    }
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const login = await signDeriveLogin(deriveConnectedWallet)
      const list = await listDeriveSubaccounts(
        workingAccount.accountId,
        deriveConnectedWallet,
        login
      )
      const selection: SelectionState = { ...emptySelection(), selected: pending }
      await registerDeriveKeyFor(workingAccount.accountId, deriveConnectedWallet, selection, list)
      const result = await linkDeriveSubaccounts(
        workingAccount.accountId,
        deriveConnectedWallet,
        selection
      )
      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      if (!isMountedRef.current) return
      setDeriveList(list)
      setDeriveSelection(emptySelection())
      // Keep the slots that already succeeded: this run only re-attempted the
      // ones that needed authorising, and dropping the rest would read as
      // having lost them.
      setDeriveLinkResult({
        slots: [...deriveLinkResult.slots.filter((slot) => slot.ok), ...result.slots],
      })
    } catch (error) {
      console.error('Failed to authorize created Derive subaccounts:', error)
      if (!isMountedRef.current) return
      setErrorMessage(extractErrorMessage(error, t('deriveLinkError')))
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      if (isMountedRef.current) setIsSubmitting(false)
    }
  }

  // Leave the results screen. The subaccounts are already linked server-side.
  // When the initiating account is ACTIVE, treat it as the completed
  // connection. When it isn't yet (activation can lag the link by up to ~1
  // min), don't strand the user on the results screen: acknowledge the pending
  // verification with a toast and complete through the same close/route path,
  // without persisting a still-verifying account as the selected exchange.
  const completeActivatedAccount = (account: PublicAccount) => {
    setSavedExchange(account)
    setReadyAccount(account)
    trackAccountConnected(account, trackedConnectionsRef.current)
    onComplete?.(account)
    if (isPageLayout) router.push('/trade')
  }
  const completeActivatedAccountRef = useRef(completeActivatedAccount)
  completeActivatedAccountRef.current = completeActivatedAccount

  const handleFinishDerive = async () => {
    await queryClient.invalidateQueries({ queryKey: ['accounts'] })
    const { data } = await refetchAccounts()
    const account = data?.accounts.find((item) => item.accountId === workingAccount?.accountId)
    if (account?.status === AccountStatus.ACCOUNT_STATUS_ACTIVE) {
      completeActivatedAccount(account)
    } else if (isPageLayout) {
      // Onboarding: wait for activation here (polled below). Routing to /trade with no
      // active account only bounced the user back to /onboarding.
      setPendingReason('activation')
      setStep('pending')
    } else {
      // The sheet runs inside the dashboard, where the user already has an account:
      // acknowledge and close; the account appears once Derive activates it.
      toast.info(t('deriveActivationPending'))
      if (account) onComplete?.(account)
    }
  }

  // Poll a still-activating account straight from the network (see waitForVerifiedAccount
  // for why not through the query cache) until it turns ACTIVE, then finish exactly as an
  // already-active one would. "Check again" restarts the window via `activationCheck`.
  useEffect(() => {
    if (step !== 'pending' || pendingReason !== 'activation' || !workingAccount) return
    const accountId = workingAccount.accountId
    let cancelled = false
    void (async () => {
      for (let attempt = 0; attempt < ACTIVATION_POLL_ATTEMPTS && !cancelled; attempt++) {
        try {
          const { accounts: latest } = await fetchUserAccounts()
          const account = latest.find((item) => item.accountId === accountId)
          if (cancelled) return
          if (account?.status === AccountStatus.ACCOUNT_STATUS_ACTIVE) {
            await queryClient.invalidateQueries({ queryKey: ['accounts'] })
            if (cancelled) return
            toast.success(t('accountConnectedSuccessfully'))
            completeActivatedAccountRef.current(account)
            return
          }
        } catch {
          // A failed poll is just one missed beat; keep waiting.
        }
        await new Promise((resolve) => setTimeout(resolve, ACTIVATION_POLL_INTERVAL_MS))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activationCheck, pendingReason, queryClient, step, t, workingAccount])

  // Submit the picker's selection. Any wallet signing already happened
  // per-slot inside LighterSubaccountPicker (prepare/sign/confirm), so this
  // just submits the resolved keys and advances straight to the per-slot
  // results screen — same shape as Hyperliquid's link submit.
  const handleLighterLinkSubmit = async () => {
    if (!workingAccount) return
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const result = await linkLighterSubaccounts(
        workingAccount.accountId,
        lighterL1Address,
        lighterSelection
      )
      // A successful link creates/links accounts server-side. Invalidate now,
      // before showing results, so dismissing the sheet straight from the
      // results screen still leaves a fresh account list behind.
      await queryClient.invalidateQueries({ queryKey: ['accounts'] })
      if (!isMountedRef.current) return
      setLighterLinkResult(result)
      setStep('lighter-results')
    } catch (error) {
      console.error('Failed to link Lighter subaccounts:', error)
      if (!isMountedRef.current) return
      setErrorMessage(extractErrorMessage(error, t('lighterLinkError')))
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      if (isMountedRef.current) setIsSubmitting(false)
    }
  }

  // "Adjust selection" on the results screen. The previous lighterList/
  // selection are stale — just-linked rows lack their alreadyLinked flag.
  // Re-list from the server and reset the selection before returning.
  const refreshLighterPicker = async () => {
    if (!workingAccount) return
    setIsSubmitting(true)
    setErrorMessage('')
    try {
      const list = await listLighterSubaccounts(workingAccount.accountId, lighterL1Address)
      if (!isMountedRef.current) return
      setLighterList(list)
      setLighterSelection(emptyLighterSelection())
      setStep('lighter-picker')
    } catch (error) {
      console.error('Failed to refresh Lighter subaccounts:', error)
      if (!isMountedRef.current) return
      setErrorMessage(extractErrorMessage(error, t('lighterLinkError')))
      toast.error(t('errors.failedConnectAccount'))
    } finally {
      if (isMountedRef.current) setIsSubmitting(false)
    }
  }

  // Leave the results screen — the sub-accounts are already linked
  // server-side, so this only needs to resolve the initiating account's own
  // verification, same as every other connect path.
  const handleFinishLighter = async () => {
    if (!workingAccount) return
    await finalizeAccountVerification(workingAccount.accountId)
  }

  const handleBackToLogin = () => {
    const onboardingWindow = window as Window & { __isForcedLogout?: boolean }
    onboardingWindow.__isForcedLogout = true
    logout()
  }

  const handleBackToTrade = () => {
    router.push(fromTradePath || '/trade')
  }

  return (
    <div
      className={cn(
        'relative flex w-full flex-col gap-6 text-white',
        isPageLayout && 'text-foreground gap-4 text-center',
        className
      )}
    >
      {isPageLayout && cameFromTrade && (
        <button
          type="button"
          onClick={handleBackToTrade}
          disabled={isPreparing || isSubmitting}
          className="text-foreground/60 hover:text-foreground flex cursor-pointer items-center gap-1.5 self-start text-sm transition-colors disabled:opacity-50"
        >
          <ArrowLeftIcon className="size-4" />
          {t('backToTrade')}
        </button>
      )}

      <div className={cn(['flex items-start gap-3', isPageLayout && 'justify-center text-center'])}>
        {headerLeading && <div className="mt-1 shrink-0">{headerLeading}</div>}
        <div className={cn('flex min-w-0 flex-col gap-2', isPageLayout && 'gap-1.5')}>
          <h1 className="font-host-grotesk text-2xl font-semibold tracking-tight">{title}</h1>
          <p
            className={cn([
              'text-sm tracking-tight text-white/60',
              isPageLayout && 'text-foreground/60',
            ])}
          >
            {step === 'credentials' && workingAccount
              ? isHyperliquid
                ? t('hyperliquidAuthorizeSubtitle')
                : oauthConfig && !showManualKeys
                  ? t('oauthDescription', { exchange: workingAccount.exchangeLabel })
                  : t('manualDescription')
              : subtitle}
          </p>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 'select' && (
          <motion.div
            key="select"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
          >
            <div className="flex flex-col gap-3">
              <p
                className={cn([
                  'text-sm font-medium text-white/60',
                  isPageLayout && 'text-foreground/60 text-left',
                ])}
              >
                {t('whichExchange')}
              </p>
              <ExchangeSelectPopover
                open={exchangePopoverOpen}
                onOpenChange={setExchangePopoverOpen}
                options={exchangeOptions}
                checkingPlan={checkingPlan}
                selectedExchange={selectedExchange}
                isPageLayout={isPageLayout}
                popoverContainer={popoverContainer}
                onSelect={handleSelectExchange}
                onUpgrade={(venue) => {
                  setExchangePopoverOpen(false)
                  if (onRequestUpgrade) onRequestUpgrade(venue)
                  else router.push(BILLING_ROUTE)
                }}
              />
            </div>

            {selectedExchange && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label
                    className={cn(['text-sm text-white/60', isPageLayout && 'text-foreground/60'])}
                  >
                    {t('accountName')}
                  </label>
                </div>
                <Input
                  value={accountName}
                  onChange={(event) => setAccountName(event.target.value)}
                  placeholder={selectedExchange.label}
                  variant="default"
                  inputClassName={cn([
                    'h-9 border-0 bg-white/5 placeholder:text-white/30',
                    isPageLayout &&
                      'text-foreground bg-foreground/5 placeholder:text-foreground/30',
                  ])}
                  disabled={isPreparing}
                />
                {!accountName.trim() && (
                  <p
                    className={cn([
                      'text-left text-xs text-white/40',
                      isPageLayout && 'text-foreground/40',
                    ])}
                  >
                    {t('leaveEmpty', {
                      name: selectedDisplayName || t('selectedAccountNameFallback'),
                    })}
                  </p>
                )}
              </div>
            )}

            {errorMessage && <p className="text-chart-red text-sm">{errorMessage}</p>}

            <Button
              variant="basic"
              type="button"
              className="mx-auto w-fit"
              disabled={!selectedExchange || isPreparing}
              onClick={handlePrepareAccount}
            >
              {isPreparing ? t('preparing') : t('continue')}
            </Button>
          </motion.div>
        )}

        {step === 'credentials' && workingAccount && oauthConfig && !showManualKeys && (
          <motion.div
            key="oauth"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
          >
            <WorkingAccountCard
              workingAccount={workingAccount}
              isPageLayout={isPageLayout}
              isSubmitting={isSubmitting}
              onChange={() => setStep('select')}
            />

            <p
              className={cn([
                'text-left text-sm text-white/60',
                isPageLayout && 'text-foreground/60',
              ])}
            >
              {t('oauthInstruction', { exchange: workingAccount.exchangeLabel })}
            </p>

            {errorMessage && <p className="text-chart-red text-sm">{errorMessage}</p>}

            <Button
              variant="basic"
              type="button"
              className="mx-auto w-fit"
              onClick={handleOauthConnect}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t('connecting')
                : t('connectWith', { exchange: workingAccount.exchangeLabel })}
            </Button>

            {workingAccount.authMethod !== AccountAuthMethod.ACCOUNT_AUTH_METHOD_APP_USER && (
              <button
                type="button"
                className={cn([
                  'mx-auto w-fit text-sm text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline',
                  isPageLayout && 'text-foreground/60 hover:text-foreground',
                ])}
                onClick={() => {
                  setErrorMessage('')
                  setShowManualKeys(true)
                }}
                disabled={isSubmitting}
              >
                {t('enterApiKeysInstead')}
              </button>
            )}
          </motion.div>
        )}

        {step === 'credentials' && workingAccount && isHyperliquid && (
          <motion.div
            key="hyperliquid"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
          >
            <WorkingAccountCard
              workingAccount={workingAccount}
              isPageLayout={isPageLayout}
              isSubmitting={isSubmitting}
              onChange={() => setStep('select')}
            />

            <p
              className={cn([
                'text-left text-sm text-white/60',
                isPageLayout && 'text-foreground/60',
              ])}
            >
              {t('hyperliquidConnectIntro')}
            </p>
            <div
              className={cn([
                'border-foreground/15 bg-foreground/8 flex flex-col gap-3 border p-3 text-left',
              ])}
            >
              <p
                className={cn([
                  'text-xs leading-relaxed text-white/70',
                  isPageLayout && 'text-foreground/70',
                ])}
              >
                {t('hyperliquidFundWarning')}
              </p>
              <a
                href="https://app.hyperliquid.xyz/"
                target="_blank"
                rel="noreferrer"
                className="text-foreground w-fit text-xs font-medium underline underline-offset-2 hover:opacity-80"
              >
                {t('hyperliquidDepositLink')}
              </a>
              <label className="flex cursor-pointer items-start gap-2">
                <Checkbox
                  checked={hasFunded}
                  onCheckedChange={(checked) => setHasFunded(checked === true)}
                  disabled={isSubmitting}
                  className="mt-0.5"
                />
                <span
                  className={cn(['text-xs text-white/70', isPageLayout && 'text-foreground/70'])}
                >
                  {t('hyperliquidFundedConfirm')}
                </span>
              </label>
            </div>

            {errorMessage && <p className="text-chart-red text-sm">{errorMessage}</p>}

            <Button
              variant="basic"
              type="button"
              className="mx-auto w-fit"
              onClick={handleHyperliquidConnect}
              disabled={isSubmitting || !hasFunded}
            >
              {isSubmitting ? t('hyperliquidAuthorizing') : t('hyperliquidConnectButton')}
            </Button>
          </motion.div>
        )}

        {step === 'derive-picker' && workingAccount && deriveList && (
          <motion.div
            key="derive-picker"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
          >
            <WorkingAccountCard
              workingAccount={workingAccount}
              isPageLayout={isPageLayout}
              isSubmitting={isSubmitting}
              onChange={() => setStep('select')}
            />

            {deriveList.subaccounts.length > 0 && (
              <p
                className={cn([
                  'text-left text-sm text-white/60',
                  isPageLayout && 'text-foreground/60',
                ])}
              >
                {t('derivePickerIntro')}
              </p>
            )}

            {errorMessage && <p className="text-chart-red text-sm">{errorMessage}</p>}

            {deriveList.subaccounts.length === 0 ? (
              <DeriveDepositPrompt
                onRefresh={refreshDerivePicker}
                refreshing={isSubmitting}
                isPageLayout={isPageLayout}
              />
            ) : (
              <DeriveSubaccountPicker
                subaccounts={deriveList.subaccounts}
                remainingSlots={deriveList.remainingAccountSlots}
                initiatingNeedsLink={initiatingNeedsLink}
                initiatingSubaccountId={initiatingSubaccountId}
                workingAccountId={workingAccount.accountId}
                maxSlotsPerLink={deriveList.maxSlotsPerLink}
                createAmountUsd={deriveList.subaccountCreateAmountUsd}
                selection={deriveSelection}
                onChange={setDeriveSelection}
                onSubmit={handleDeriveLinkSubmit}
                submitting={isSubmitting}
                isPageLayout={isPageLayout}
              />
            )}
          </motion.div>
        )}

        {step === 'derive-results' && deriveLinkResult && (
          <DeriveLinkResults
            key="derive-results"
            slots={deriveLinkResult.slots}
            isPageLayout={isPageLayout}
            submitting={isSubmitting}
            errorMessage={errorMessage}
            onAuthorize={handleDeriveAuthorizeCreated}
            onFinish={handleFinishDerive}
            onBack={refreshDerivePicker}
          />
        )}

        {step === 'hyperliquid-picker' && workingAccount && hyperliquidList && (
          <motion.div
            key="hyperliquid-picker"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
          >
            <WorkingAccountCard
              workingAccount={workingAccount}
              isPageLayout={isPageLayout}
              isSubmitting={isSubmitting}
              onChange={() => setStep('select')}
            />

            <p
              className={cn([
                'text-left text-sm text-white/60',
                isPageLayout && 'text-foreground/60',
              ])}
            >
              {hyperliquidWalletPreconnected
                ? t('hyperliquidWalletAlreadyConnected')
                : t('hyperliquidPickerIntro')}
            </p>

            {errorMessage && <p className="text-chart-red text-sm">{errorMessage}</p>}

            <HyperliquidSubaccountPicker
              subaccounts={hyperliquidList.subaccounts}
              remainingSlots={hyperliquidList.remainingAccountSlots}
              selection={hyperliquidSelection}
              onChange={setHyperliquidSelection}
              onSubmit={handleHyperliquidLinkSubmit}
              submitting={isSubmitting}
              isPageLayout={isPageLayout}
            />
          </motion.div>
        )}

        {step === 'hyperliquid-results' && hyperliquidLinkResult && (
          <HyperliquidLinkResults
            key="hyperliquid-results"
            slots={hyperliquidLinkResult.slots}
            isPageLayout={isPageLayout}
            submitting={isSubmitting}
            onFinish={handleFinishHyperliquid}
            onBack={refreshHyperliquidPicker}
          />
        )}

        {step === 'lighter-picker' && workingAccount && lighterList && (
          <motion.div
            key="lighter-picker"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
          >
            <WorkingAccountCard
              workingAccount={workingAccount}
              isPageLayout={isPageLayout}
              isSubmitting={isSubmitting}
              onChange={() => setStep('select')}
            />

            {errorMessage && <p className="text-chart-red text-sm">{errorMessage}</p>}

            <LighterSubaccountPicker
              subaccounts={lighterList.subaccounts}
              accountId={workingAccount.accountId}
              l1Address={lighterL1Address}
              selection={lighterSelection}
              onChange={setLighterSelection}
              onSubmit={handleLighterLinkSubmit}
              submitting={isSubmitting}
              isPageLayout={isPageLayout}
              remainingSlots={lighterList.remainingAccountSlots}
            />
          </motion.div>
        )}

        {step === 'lighter-results' && lighterLinkResult && (
          <LighterLinkResults
            key="lighter-results"
            slots={lighterLinkResult.slots}
            isPageLayout={isPageLayout}
            submitting={isSubmitting}
            onFinish={handleFinishLighter}
            onBack={refreshLighterPicker}
          />
        )}

        {step === 'credentials' &&
          workingAccount &&
          (!oauthConfig || showManualKeys) &&
          !isHyperliquid &&
          !isDerive &&
          !isLighter &&
          workingAccount.authMethod !== AccountAuthMethod.ACCOUNT_AUTH_METHOD_APP_USER && (
            <motion.form
              key="credentials"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              onSubmit={credentialsForm.handleSubmit(handleSubmitCredentials)}
              className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
            >
              <WorkingAccountCard
                workingAccount={workingAccount}
                isPageLayout={isPageLayout}
                isSubmitting={isSubmitting}
                onChange={() => setStep('select')}
              />

              <div className="flex flex-col gap-2 text-left">
                <label
                  className={cn(['text-sm text-white/60', isPageLayout && 'text-foreground/60'])}
                >
                  {t('apiKey')}
                </label>
                <Input
                  type="text"
                  placeholder={t('enterApiKey')}
                  autoComplete="off"
                  data-1p-ignore
                  {...credentialsForm.register('apiKey')}
                  variant="default"
                  inputClassName={cn([
                    'h-9 border-0 bg-white/5 placeholder:text-white/30',
                    isPageLayout &&
                      'text-foreground bg-foreground/5 placeholder:text-foreground/60',
                  ])}
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex flex-col gap-2 text-left">
                <label
                  className={cn(['text-sm text-white/60', isPageLayout && 'text-foreground/60'])}
                >
                  {t('apiSecret')}
                </label>
                <div className="relative">
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    placeholder={t('enterApiSecret')}
                    autoComplete="new-password"
                    data-1p-ignore
                    {...credentialsForm.register('apiSecret')}
                    variant="default"
                    inputClassName={cn([
                      'h-9 border-0 bg-white/5 placeholder:text-white/30',
                      isPageLayout &&
                        'text-foreground bg-foreground/5 placeholder:text-foreground/60',
                    ])}
                    disabled={isSubmitting}
                  />
                  <Button
                    aria-label={showSecret ? tCommon('hideSecret') : tCommon('showSecret')}
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute top-0 right-0 h-9 w-9 hover:bg-transparent"
                    onClick={() => setShowSecret((value) => !value)}
                    disabled={isSubmitting}
                  >
                    {showSecret ? (
                      <EyeIcon
                        className={cn([
                          'size-4 text-white/40',
                          isPageLayout && 'text-foreground/60',
                        ])}
                      />
                    ) : (
                      <EyeOffIcon
                        className={cn([
                          'size-4 text-white/40',
                          isPageLayout && 'text-foreground/60',
                        ])}
                      />
                    )}
                  </Button>
                </div>
              </div>

              {errorMessage && <p className="text-chart-red text-sm">{errorMessage}</p>}

              <Button
                variant="basic"
                type="submit"
                className={cn('mx-auto mt-5 w-fit', isPageLayout && 'mt-2')}
                disabled={!credentialsForm.formState.isValid || isSubmitting}
              >
                {isSubmitting ? t('verifying') : t('continue')}
              </Button>

              {oauthConfig && (
                <button
                  type="button"
                  className={cn([
                    'mx-auto w-fit text-sm text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline',
                    isPageLayout && 'text-foreground/60 hover:text-foreground',
                  ])}
                  onClick={() => {
                    setErrorMessage('')
                    setShowManualKeys(false)
                  }}
                  disabled={isSubmitting}
                >
                  {t('connectWithInstead', { exchange: workingAccount.exchangeLabel })}
                </button>
              )}

              <Separator
                className={cn(['my-4 bg-white/10', isPageLayout && 'bg-foreground/10 my-2'])}
              />

              <ApiKeySetupGuide
                tradeVenue={workingAccount.tradeVenue}
                publicIp={workingAccount.publicIp}
                copied={copied}
                isPageLayout={isPageLayout}
                onCopyIp={handleCopyIp}
              />
              <ApiKeyGuideHelpText
                tradeVenue={workingAccount.tradeVenue}
                isPageLayout={isPageLayout}
              />
            </motion.form>
          )}

        {step === 'credentials' &&
          workingAccount &&
          !oauthConfig &&
          workingAccount.authMethod === AccountAuthMethod.ACCOUNT_AUTH_METHOD_APP_USER && (
            <motion.div
              key="appuser-no-oauth"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
            >
              <WorkingAccountCard
                workingAccount={workingAccount}
                isPageLayout={isPageLayout}
                isSubmitting={isSubmitting}
                onChange={() => setStep('select')}
              />
              <p className="text-chart-red text-sm">{t('oauthOnlyAccount')}</p>
            </motion.div>
          )}

        {step === 'papertrade-sign' && workingAccount && papertradeWallet && (
          <motion.div
            key="papertrade-sign"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
          >
            <WorkingAccountCard
              workingAccount={workingAccount}
              isPageLayout={isPageLayout}
              isSubmitting={isSubmitting}
              onChange={() => setStep('select')}
            />
            <p className={cn(['text-sm text-white/60', isPageLayout && 'text-foreground/60'])}>
              {t('papertradeConnectIntro')}
            </p>
            <PapertradeSignStep
              key={workingAccount.accountId}
              accountId={workingAccount.accountId}
              wallet={papertradeWallet}
              onDone={() => finalizeAccountVerification(workingAccount.accountId)}
            />
          </motion.div>
        )}

        {step === 'success' && workingAccount && (
          <WizardResult
            key="success"
            tone="success"
            title={t('successTitle')}
            description={t('successDescription', { account: workingAccount.accountName })}
            isPageLayout={isPageLayout}
            canGoToTrade={Boolean(readyAccount)}
          />
        )}

        {step === 'pending' && workingAccount && (
          <WizardResult
            key="pending"
            tone="pending"
            title={t('pendingTitle')}
            description={
              pendingReason === 'activation'
                ? t('activationPendingDescription', { account: workingAccount.accountName })
                : t('pendingDescription', { account: workingAccount.accountName })
            }
            isPageLayout={isPageLayout}
            canGoToTrade={false}
            onRetry={
              pendingReason === 'activation'
                ? () => setActivationCheck((n) => n + 1)
                : handleRetryCredentials
            }
            retryLabel={pendingReason === 'activation' ? t('checkAgain') : undefined}
          />
        )}
      </AnimatePresence>

      {isPageLayout && !cameFromTrade && (
        <Button
          type="button"
          variant="subtle"
          className="mx-auto h-8 px-3"
          onClick={handleBackToLogin}
          disabled={isPreparing || isSubmitting}
        >
          <LogOutIcon className="size-4" />
          {t('logOutAndSwitchAccount')}
        </Button>
      )}
    </div>
  )
}

function ApiKeySetupGuide({
  tradeVenue,
  publicIp,
  copied,
  isPageLayout,
  onCopyIp,
}: {
  tradeVenue: TradeVenue
  publicIp: string
  copied: boolean
  isPageLayout: boolean
  onCopyIp: () => void
}) {
  const tCommon = useTranslations('common')
  const guide = useApiKeyGuide(tradeVenue)
  const t = useTranslations('accountConnection')

  // No guide means the venue mints credentials itself, or GetVenues hasn't
  // answered and there is no fallback. Render nothing rather than an empty
  // checklist a user might take as "no permissions needed".
  if (!guide) return null

  return (
    <section
      className={cn('flex flex-col items-center gap-5 text-center', isPageLayout && 'gap-3')}
    >
      <h2 className="font-host-grotesk text-xl font-semibold tracking-tight">
        {t('apiGuideTitle')}
      </h2>
      <div
        className={cn([
          'flex w-full flex-col gap-4 border border-white/10 bg-white/5 p-4 text-left',
          isPageLayout && 'border-foreground/50 text-foreground/60 gap-3 bg-transparent p-3',
        ])}
      >
        <p className="text-sm">
          {t('createApiKeyPrefix')}{' '}
          <a
            href={guide.createKeyUrl}
            target="_blank"
            rel="noreferrer"
            className={cn([
              'underline underline-offset-2 hover:text-white',
              isPageLayout && 'hover:text-foreground',
            ])}
          >
            {t('here')}
          </a>{' '}
          {t('createApiKeySuffix')}
        </p>

        {tradeVenue === TradeVenue.TRADE_VENUE_BYBIT && (
          <div
            className={cn([
              'border-foreground/15 bg-foreground/8 flex flex-col gap-2 border p-3 text-sm',
              isPageLayout && 'border-foreground/15 bg-foreground/8 text-foreground/60',
            ])}
          >
            <p className={cn('font-medium text-white', isPageLayout && 'text-foreground')}>
              {t('bybitThirdPartyTitle')}
            </p>
            <ul className="flex flex-col gap-1.5">
              <li>{t('bybitApiUsage')}</li>
              <li>{t('bybitApplicationName')}</li>
              <li>{t('bybitApiPermissions')}</li>
            </ul>
          </div>
        )}

        <ul className="flex flex-col gap-2">
          {guide.permissions.map((permission) => (
            <li
              key={permission.label}
              className="text-foreground/80 flex items-center gap-3 text-sm"
            >
              <CheckIcon className="size-4 shrink-0" />
              <span>{permission.label}</span>
            </li>
          ))}
        </ul>

        {/*
          No bybitTestnetNote branch any more: createKeyUrl is already the
          testnet host on dev, so telling the user to go there instead was
          both redundant and, on prod, wrong.
        */}
        {guide.note && <p className={cn('text-foreground/50 text-sm')}>{guide.note}</p>}

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-foreground">
            {publicIp ? t('requiredIpWhitelist') : t('requiredIpWhitelistWillBeAssigned')}
          </span>
          <div
            className={cn([
              'bg-foreground/5 text-foreground/80 flex min-w-0 items-center gap-2 px-2 py-1',
              isPageLayout && 'text-foreground bg-foreground/10',
            ])}
          >
            <code className="truncate">{publicIp || t('afterCredentialsUpdate')}</code>
            <Button
              aria-label={tCommon('copyIpAddress')}
              type="button"
              variant="ghost"
              size="icon"
              className="size-5 p-0 hover:bg-transparent"
              onClick={onCopyIp}
              disabled={!publicIp}
            >
              {copied ? (
                <CheckIcon className="text-foreground size-3.5" />
              ) : (
                <CopyIcon className={cn(['text-foreground/60 size-3.5'])} />
              )}
            </Button>
          </div>
        </div>
        {!publicIp && (
          <p className={cn('text-foreground/50 text-sm')}>{t('newWhitelistIpNotice')}</p>
        )}
      </div>
    </section>
  )
}

function ApiKeyGuideHelpText({
  tradeVenue,
  isPageLayout,
}: {
  tradeVenue: TradeVenue
  isPageLayout: boolean
}) {
  const t = useTranslations('accountConnection')
  const guide = useApiKeyGuide(tradeVenue)
  const guideUrl = guide?.guideUrl ?? guide?.createKeyUrl

  if (!guideUrl) return null

  return (
    <p className={cn(['text-foreground -mt-2 text-center text-sm'])}>
      {t('furtherHelp')}{' '}
      <a
        href={guideUrl}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 hover:text-current"
      >
        {t('thisGuide')}
      </a>
      .
    </p>
  )
}

function WizardResult({
  title,
  description,
  tone,
  isPageLayout,
  canGoToTrade = false,
  onRetry,
  retryLabel,
}: {
  title: string
  description: string
  tone: 'success' | 'pending'
  isPageLayout?: boolean
  canGoToTrade?: boolean
  /** The retry button's label; defaults to "Re-enter API keys". */
  retryLabel?: string
  onRetry?: () => void
}) {
  const t = useTranslations('accountConnection')
  const router = useRouter()
  const shouldRedirectToTrade = isPageLayout && tone === 'success' && canGoToTrade
  const [redirectSecondsLeft, setRedirectSecondsLeft] = useState(1)

  useEffect(() => {
    if (!shouldRedirectToTrade) return

    setRedirectSecondsLeft(1)
    const timeoutId = window.setTimeout(() => {
      router.push('/trade')
    }, 1000)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [router, shouldRedirectToTrade])

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      className={cn('flex flex-col items-center gap-3 px-4 py-8 text-center')}
    >
      <div
        className={cn(
          'flex size-12 items-center justify-center rounded-full',
          tone === 'success' ? 'bg-foreground/10' : isPageLayout ? 'bg-foreground/5' : 'bg-white/10'
        )}
      >
        <CheckIcon
          className={cn(
            'size-6',
            tone === 'success'
              ? 'text-chart-mint'
              : isPageLayout
                ? 'text-black/60'
                : 'text-white/70'
          )}
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">{title}</p>
        <p className={cn(['text-sm text-white/60', isPageLayout && 'text-foreground/60'])}>
          {description}
        </p>
      </div>
      {isPageLayout && tone === 'pending' && (
        <p className="text-foreground/40 mt-2 text-xs">{t('checkingAccountStatus')}</p>
      )}
      {tone === 'pending' && onRetry && (
        <Button variant="ghost" type="button" className="mt-2" onClick={onRetry}>
          {retryLabel ?? t('retryCredentials')}
        </Button>
      )}
      {shouldRedirectToTrade && (
        <div className="mt-3 flex flex-col items-center gap-2">
          <Button variant="basic" type="button" onClick={() => router.push('/trade')}>
            {t('goToTradeWithSeconds', { seconds: redirectSecondsLeft })}
          </Button>
          <p className="text-foreground/40 text-xs">
            {t('redirectingToTrade', { seconds: redirectSecondsLeft })}
          </p>
        </div>
      )}
    </motion.div>
  )
}

// Per-slot outcome of a batch subaccount link. Reaped-sibling / creation
// failures arrive with an empty accountName (and possibly subaccountId "0"),
// so we fall back to the subaccount id and always surface the error verbatim.
function DeriveLinkResults({
  slots,
  isPageLayout,
  submitting,
  errorMessage,
  onAuthorize,
  onFinish,
  onBack,
}: {
  slots: DeriveLinkSubaccountsResponse['slots']
  isPageLayout: boolean
  submitting: boolean
  errorMessage: string
  onAuthorize: () => void
  onFinish: () => void
  onBack: () => void
}) {
  const t = useTranslations('accountConnection')
  const anyFailed = slots.some((slot) => !slot.ok)
  // A failed slot that still names a subaccount is one the venue refused to
  // trade rather than one that never came into being — a create, whose id did
  // not exist when the customer signed. That is the only failure a second
  // signature fixes.
  const needsAuthorization = slots.some((slot) => !slot.ok && slot.subaccountId !== '0')

  return (
    <motion.div
      key="derive-results"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
    >
      <p className="text-center text-sm font-medium">
        {anyFailed ? t('deriveResultsPartialTitle') : t('deriveResultsTitle')}
      </p>

      <div className="flex flex-col gap-2">
        {slots.map((slot, index) => (
          <div
            key={`${slot.subaccountId}-${slot.accountId}-${index}`}
            className={cn(
              'flex items-start gap-3 border p-3 text-left',
              slot.ok
                ? cn(
                    'border-white/10 bg-white/5',
                    isPageLayout && 'border-foreground/10 bg-foreground/5'
                  )
                : 'border-chart-red/40 bg-chart-red/10'
            )}
          >
            <div className="mt-0.5 shrink-0">
              {slot.ok ? (
                <CheckIcon className="text-chart-mint size-4" />
              ) : (
                <XIcon className="text-chart-red size-4" />
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-medium">
                {slot.accountName ||
                  (slot.subaccountId === '0'
                    ? t('deriveNewSubaccountLabel')
                    : t('deriveSubaccountLabel', { id: slot.subaccountId }))}
              </span>
              {slot.accountName && (
                <span className={cn('text-xs text-white/50', isPageLayout && 'text-foreground/50')}>
                  {t('deriveSubaccountLabel', { id: slot.subaccountId })}
                </span>
              )}
              {!slot.ok && slot.error && (
                <span className="text-chart-red text-xs">{slot.error}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {needsAuthorization && (
        <p className={cn('text-left text-sm text-white/60', isPageLayout && 'text-foreground/60')}>
          {t('deriveAuthorizeCreatedNote')}
        </p>
      )}

      {errorMessage && <p className="text-chart-red text-sm">{errorMessage}</p>}

      <div className="flex flex-col items-center gap-2">
        {needsAuthorization && (
          <Button
            variant="basic"
            type="button"
            className="w-fit"
            onClick={onAuthorize}
            disabled={submitting}
          >
            {submitting ? t('deriveAuthorizeCreatedLoading') : t('deriveAuthorizeCreated')}
          </Button>
        )}
        <Button
          variant={needsAuthorization ? 'ghost' : 'basic'}
          type="button"
          className="w-fit"
          onClick={onFinish}
          disabled={submitting}
        >
          {isPageLayout ? t('deriveGoToTrade') : t('deriveResultsDone')}
        </Button>
        {anyFailed && (
          <Button variant="ghost" type="button" onClick={onBack} disabled={submitting}>
            {submitting ? t('deriveResultsBackLoading') : t('deriveResultsBack')}
          </Button>
        )}
      </div>
    </motion.div>
  )
}

// Per-slot outcome of a batch sub-account link. Unlike DeriveLinkedSlot,
// HyperliquidLinkedSlot carries no `ok`/accountName field — success is
// `error === ''`, and the sub-account address is the only stable label.
function HyperliquidLinkResults({
  slots,
  isPageLayout,
  submitting,
  onFinish,
  onBack,
}: {
  slots: HyperliquidLinkSubaccountsResponse['slots']
  isPageLayout: boolean
  submitting: boolean
  onFinish: () => void
  onBack: () => void
}) {
  const t = useTranslations('accountConnection')
  const anyFailed = slots.some((slot) => Boolean(slot.error))

  return (
    <motion.div
      key="hyperliquid-results"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
    >
      <p className="text-center text-sm font-medium">
        {anyFailed ? t('hyperliquidResultsPartialTitle') : t('hyperliquidResultsTitle')}
      </p>

      <div className="flex flex-col gap-2">
        {slots.map((slot, index) => {
          const ok = !slot.error
          return (
            <div
              key={`${slot.subaccountAddress}-${index}`}
              className={cn(
                'flex items-start gap-3 border p-3 text-left',
                ok
                  ? cn(
                      'border-white/10 bg-white/5',
                      isPageLayout && 'border-foreground/10 bg-foreground/5'
                    )
                  : 'border-chart-red/40 bg-chart-red/10'
              )}
            >
              <div className="mt-0.5 shrink-0">
                {ok ? (
                  <CheckIcon className="text-chart-mint size-4" />
                ) : (
                  <XIcon className="text-chart-red size-4" />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-medium">{slot.subaccountAddress}</span>
                {!ok && slot.error && <span className="text-chart-red text-xs">{slot.error}</span>}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button
          variant="basic"
          type="button"
          className="w-fit"
          onClick={onFinish}
          disabled={submitting}
        >
          {isPageLayout ? t('hyperliquidGoToTrade') : t('hyperliquidResultsDone')}
        </Button>
        {anyFailed && (
          <Button variant="ghost" type="button" onClick={onBack} disabled={submitting}>
            {submitting ? t('hyperliquidResultsBackLoading') : t('hyperliquidResultsBack')}
          </Button>
        )}
      </div>
    </motion.div>
  )
}

// Per-slot outcome of a batch sub-account link. LighterLinkedSlot carries
// both `ok` and `accountName` (like DeriveLinkedSlot) but keys off
// `accountIndex` rather than a subaccountId — every Lighter slot references
// a pre-existing sub-account returned by the list call, so there's no
// Derive-style "new subaccount" sentinel to special-case.
function LighterLinkResults({
  slots,
  isPageLayout,
  submitting,
  onFinish,
  onBack,
}: {
  slots: LighterLinkSubaccountsResponse['slots']
  isPageLayout: boolean
  submitting: boolean
  onFinish: () => void
  onBack: () => void
}) {
  const t = useTranslations('accountConnection')
  const anyFailed = slots.some((slot) => !slot.ok)

  // Partner Attribution consent, gathered here rather than left to the
  // settings dialog: an account without it is BLOCKED from trading by the
  // venue service, so finishing onboarding without it hands the user an
  // account that looks connected and refuses every order. That's why Done
  // below stays disabled until it lands — there is no useful "skip".
  //
  // Fires on arrival rather than behind a button: the link step the user just
  // completed signed with the wallet, which leaves the manual-action window
  // open, so the prompt can be raised directly. Retry re-opens that window
  // itself, since by then the activation is long gone.
  //
  // One wallet signature per linked account — the on-chain ApproveIntegrator
  // is per-account, so a batch link needs one each, run sequentially.
  const { prepare, signAndConfirm } = useLighterIntegratorConsent()
  const linked = slots.filter((slot) => slot.ok && slot.accountId)
  const linkedIds = linked.map((slot) => slot.accountId).join(',')
  const [consentDone, setConsentDone] = useState(false)
  const [consentBusy, setConsentBusy] = useState(false)
  const [consentError, setConsentError] = useState('')
  const [consentAttempt, setConsentAttempt] = useState(0)

  useEffect(() => {
    const accountIds = linkedIds ? linkedIds.split(',') : []
    if (accountIds.length === 0) return
    let cancelled = false

    const run = async () => {
      setConsentBusy(true)
      setConsentError('')
      try {
        for (const accountId of accountIds) {
          const prepared = await prepare(accountId)
          await signAndConfirm(prepared.preparedConsentId, prepared.personalSignText)
          if (cancelled) return
        }
        if (!cancelled) setConsentDone(true)
      } catch (e) {
        if (!cancelled) setConsentError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setConsentBusy(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [linkedIds, consentAttempt, prepare, signAndConfirm])

  const handleRetryFees = () => {
    // Synchronously first — transient user activation does not survive an await.
    try {
      beginManualWalletAction()
    } catch (e) {
      setConsentError(e instanceof Error ? e.message : String(e))
      return
    }
    setConsentAttempt((n) => n + 1)
  }

  return (
    <motion.div
      key="lighter-results"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className={cn('flex flex-col gap-5', isPageLayout && 'gap-4')}
    >
      <p className="text-center text-sm font-medium">
        {anyFailed ? t('lighterResultsPartialTitle') : t('lighterResultsTitle')}
      </p>

      <div className="flex flex-col gap-2">
        {slots.map((slot, index) => (
          <div
            key={`${slot.accountIndex}-${slot.accountId}-${index}`}
            className={cn(
              'flex items-start gap-3 border p-3 text-left',
              slot.ok
                ? cn(
                    'border-white/10 bg-white/5',
                    isPageLayout && 'border-foreground/10 bg-foreground/5'
                  )
                : 'border-chart-red/40 bg-chart-red/10'
            )}
          >
            <div className="mt-0.5 shrink-0">
              {slot.ok ? (
                <CheckIcon className="text-chart-mint size-4" />
              ) : (
                <XIcon className="text-chart-red size-4" />
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-medium">
                {slot.accountName || t('lighterSubaccountLabel', { id: slot.accountIndex })}
              </span>
              {slot.accountName && (
                <span className={cn('text-xs text-white/50', isPageLayout && 'text-foreground/50')}>
                  {t('lighterSubaccountLabel', { id: slot.accountIndex })}
                </span>
              )}
              {!slot.ok && slot.error && (
                <span className="text-chart-red text-xs">{slot.error}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {linked.length > 0 && (
        <div
          className={cn(
            'flex flex-col gap-2 border p-3 text-left',
            consentDone ? 'border-white/10 bg-white/5' : 'border-foreground/20 bg-foreground/8'
          )}
        >
          {consentDone ? (
            <span className="flex items-center gap-2 text-sm">
              <CheckIcon className="text-chart-mint size-4 shrink-0" />
              {t('lighterFeeConsentDone')}
            </span>
          ) : (
            <>
              <span className="text-sm font-medium">{t('lighterFeeConsentTitle')}</span>
              <span className="text-xs text-white/60">
                {t('lighterFeeConsentOnboardingBody', { count: linked.length })}
              </span>
              {consentError && <span className="text-chart-red text-xs">{consentError}</span>}
              {consentBusy ? (
                <span className="flex items-center gap-2 text-xs text-white/60">
                  <Loader2Icon className="size-4 animate-spin" />
                  {t('lighterFeeConsentSigning')}
                </span>
              ) : (
                <Button
                  variant="basic"
                  type="button"
                  className="w-fit"
                  onClick={handleRetryFees}
                  disabled={submitting}
                >
                  {t('lighterFeeConsentRetry')}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex flex-col items-center gap-2">
        <Button
          variant="basic"
          type="button"
          className="w-fit"
          onClick={onFinish}
          disabled={submitting || consentBusy || (linked.length > 0 && !consentDone)}
        >
          {isPageLayout ? t('lighterGoToTrade') : t('lighterResultsDone')}
        </Button>
        {/* Says why Done is disabled. Not an invitation to skip: an unapproved
            account is refused by the venue on every order, so leaving here
            without it produces a connected-looking account that can't trade. */}
        {linked.length > 0 && !consentDone && (
          <span className="text-center text-xs text-white/50">
            {t('lighterFeeConsentRequired')}
          </span>
        )}
        {anyFailed && (
          <Button variant="ghost" type="button" onClick={onBack} disabled={submitting}>
            {submitting ? t('lighterResultsBackLoading') : t('lighterResultsBack')}
          </Button>
        )}
      </div>
    </motion.div>
  )
}
